import { useEffect, useState } from 'react';
import { Box, Center, Loader, Text, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useAuthStore } from '@/stores/authStore';
import { useRoomStore } from '@/stores/roomStore';
import { useVideoCall } from '@/hooks/useVideoCall';
import { VideoGrid } from '@/components/VideoCall/VideoGrid';
import { VideoControls } from '@/components/VideoCall/VideoControls';
import { VideoGridSkeleton } from '@/components/VideoCall/VideoGridSkeleton';

interface MeetingRoomContentProps {
  roomCode: string;
  isOwner?: boolean;
}

export const MeetingRoomContent = ({ roomCode, isOwner = false }: MeetingRoomContentProps) => {
  const user = useAuthStore((state) => state.user);
  const currentRoom = useRoomStore((state) => state.currentRoom);
  const [isJoining, setIsJoining] = useState(true);
  const [hasJoinedOnce, setHasJoinedOnce] = useState(false);

  const {
    participantIds,
    isJoined,
    isCameraOn,
    isMicOn,
    joinCall,
    leaveCall,
    toggleCamera,
    toggleMicrophone,
  } = useVideoCall();

  useEffect(() => {
    const join = async () => {
      // Prevent multiple join attempts
      if (!user || !currentRoom || hasJoinedOnce) return;

      try {
        setIsJoining(true);

        // Use the actual Daily.co room URL from the room store
        await joinCall(currentRoom.roomUrl, user.name);
        setHasJoinedOnce(true);
        // Notificação de sucesso tratada em useVideoCall.tsx (evento joined-meeting)
      } catch (error) {
        console.error('Error joining call:', error);
        notifications.show({
          title: 'Erro ao conectar',
          message: 'Não foi possível entrar na chamada',
          color: 'red',
        });
      } finally {
        setIsJoining(false);
      }
    };

    join();

    return () => {
      if (isJoined) {
        leaveCall();
      }
    };
  }, [user, currentRoom]);

  if (isJoining || !isJoined) {
    return (
      <Box style={{ position: 'relative', width: '100%', height: '100%' }}>
        {/* Loading skeleton */}
        <VideoGridSkeleton />

        {/* Loading overlay */}
        <Center
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(4px)',
            zIndex: 10,
          }}
          className="fade-in"
        >
          <Stack align="center" gap="md">
            <Loader size="lg" color="blue" />
            <Text c="white" size="lg" fw={500}>
              Conectando à conferência...
            </Text>
          </Stack>
        </Center>
      </Box>
    );
  }

  return (
    <Box style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Video Grid */}
      <VideoGrid
        participantIds={participantIds}
        controls={
          <VideoControls
            isMicOn={isMicOn}
            isCameraOn={isCameraOn}
            onToggleMic={toggleMicrophone}
            onToggleCamera={toggleCamera}
            onLeave={leaveCall}
            isOwner={isOwner}
          />
        }
      />

      {/* Participant count - centered at top */}
      <Box
        style={{
          position: 'absolute',
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(10px)',
          padding: '8px 16px',
          borderRadius: 16,
          color: 'white',
          fontSize: 14,
          fontWeight: 500,
          zIndex: 10,
        }}
      >
        {participantIds.length} {participantIds.length === 1 ? 'participante' : 'participantes'}
      </Box>
    </Box>
  );
};
